import React, { useContext } from 'react';
import { BasePage, Helmet, StaticContext, LogoWithText } from '@sm/webassets';
import { Container, Typography } from '@sm/wds-react';
import { defineMessages, t } from '@sm/intl';

import HeroFormCTA from '~app/components/HeroFormCTA';
import theme from '../../styles/SurveyMonkey.theme';
import './enterprise-thank-you.scss';

const COPY = defineMessages({
  SIGN_UP_DISCLAIMER: {
    id: 'EnterpriseThankYou.SignUpDisclaimer',
    defaultMessage:
      'By clicking “Get Started”, you agree to the <a href="/mp/legal/terms-of-use/">Terms of Use</a> and <a href="/mp/legal/privacy-policy/">Privacy Policy</a>. You also agree to receive information and offers relevant to our services via email. You can opt-out of these emails in your My Account page anytime.',
    description:
      '[Type: Button][Vis: high] - A legal disclaimer containing links to legal documents Terms of Service and Privacy Policy',
  },
  SIGN_UP_CALLOUT_SUB_HEADER: {
    id: 'EnterpriseThankYou.SignUpCalloutSubHeader',
    defaultMessage:
      'Gather opinions from customers, employees, prospects, and more. Use your insights to make better, data-driven decisions.',
    description:
      "[Type: Header][Vis: high] - The Hero's subheader. A value proposition, giving a clear example of what they can get if they fill out the form below.",
  },
  POWERED_BY_TEXT: {
    id: 'EnterpriseThankYou.PoweredByText',
    defaultMessage: 'Powered by',
    description:
      '[Type: Label][Vis: med] - This figure of speech is used to introduce and display the SurveyMonkey Logo',
  },
  THANK_YOU: {
    id: 'EnterpriseThankYou.EnterpriseThankYouMessage',
    defaultMessage: 'Thank you for taking this survey',
    description:
      '[Type: Header][vis: high] - Header message on webpage. Used on a Nonbranded webpage to say thank you to survey respondents and signal the end of the survey.',
  },
  SIGN_UP_FORM_CTA_EMAIL_ADDRESS: {
    id: 'EnterpriseThankYou.SignUpFormCTAEmailAddress',
    defaultMessage: 'Email Address',
    description: '[Type: Label][Vis: high] - The Label for an email input field.',
  },
  SIGN_UP_FORM_CTA_PASSWORD: {
    id: 'EnterpriseThankYou.SignUpFormCTAPassword',
    defaultMessage: 'Password',
    description: '[Type: Label][Vis: high] - The Label for a password input field.',
  },
  SIGN_UP_FORM_CTA_SUBMIT: {
    id: 'EnterpriseThankYou.SignUpFormCTASubmit',
    defaultMessage: 'Get started',
    description:
      '[Type: Button][Vis: high] - The button users are required to click to create an account using the values they entered in the email and password feilds.',
  },
  LOGO_PLACEHOLDER: {
    id: 'EnterpriseThankYou.EnterpriseLogoPlaceholder',
    defaultMessage: 'Logo',
    description: '[Type: LogoText][Vis: low] - Logo placeholder text for a page header.',
  },
});

const HERO_FORM_CTA = {
  className: 'sm-hero-form-cta__enterprise-thank-you',
  ctaFormEmail: t(COPY.SIGN_UP_FORM_CTA_EMAIL_ADDRESS),
  ctaFormEmailPlaceholder: t(COPY.SIGN_UP_FORM_CTA_EMAIL_ADDRESS),
  ctaFormNewsletterOptOut: '',
  ctaFormPassword: t(COPY.SIGN_UP_FORM_CTA_PASSWORD),
  ctaFormPasswordPlaceholder: t(COPY.SIGN_UP_FORM_CTA_PASSWORD),
  ctaFormPrivacyConsent: 'privacy consent',
  ctaFormSubmit: t(COPY.SIGN_UP_FORM_CTA_SUBMIT),
  disclaimer: t(COPY.SIGN_UP_DISCLAIMER),
  utSource: 'enterprise_thank_you',
  utSource2: 'rebrand_ep_form',
  showThirdPartySignUpActions: false,
  showDisclaimer: false,
};

const enterpriseThankYouHeader = logoUrl => {
  return (
    <Container className="sm-enterprise-thank-you__header-outline">
      <Choose>
        <When condition={logoUrl}>
          <Container className="sm-enterprise-thank-you__header-logo" flex flexJustify="center">
            <img className="sm-enterprise-thank-you__logo" src={logoUrl} alt={t(COPY.LOGO_PLACEHOLDER)} />
          </Container>
        </When>
      </Choose>
      <Container className="sm-enterprise-thank-you__header-text" flex flexJustify="center">
        <Typography className="sm-enterprise-thank-you__header-text-inner" component="h1" align="center" color="dark">
          {t(COPY.THANK_YOU)}
        </Typography>
      </Container>
    </Container>
  );
};

const enterpriseThankYouBody = tier => {
  return (
    <Container className={`sm-enterprise-thank-you__body-outline sm-enterprise-thank-you__body-outline-tier-${tier}`}>
      <Container className="sm-enterprise-thank-you__body-powered-by-container" flex flexJustify="center">
        <Typography component="div" align="center" color="light">
          {t(COPY.POWERED_BY_TEXT)}
        </Typography>
      </Container>
      <Container flex flexJustify="center" className="sm-enterprise-thank-you__body-branding-logo">
        <LogoWithText color="alt" />
      </Container>
      <Container className="sm-enterprise-thank-you__body-branding-text" flex flexJustify="center">
        <Typography className="sm-enterprise-thank-you__body-branding-text-inner">
          {t(COPY.SIGN_UP_CALLOUT_SUB_HEADER)}
        </Typography>
      </Container>
      <Choose>
        <When condition={tier === 2}>
          <Container className="sm-enterprise-thank-you__body-sign-up-container" flex flexJustify="center">
            <HeroFormCTA className="sm-enterprise-thank-you__sign-up" {...HERO_FORM_CTA} />
          </Container>
        </When>
      </Choose>
    </Container>
  );
};

const enterpriseThankYouFooter = () => {
  return (
    <Container className="sm-enterprise-thank-you__footer">
      <Typography
        align="center"
        component="div"
        variant="bodySm"
        color="dark-muted"
        className="sm-enterprise-thank-you__disclaimer"
        dangerouslySetInnerHTML={{ __html: `${t(COPY.SIGN_UP_DISCLAIMER)}` }} // eslint-disable-line jam3/no-sanitizer-with-danger
      />
    </Container>
  );
};

const EnterpriseThankYou = () => {
  const {
    pageOptions: { tier, logo },
  } = useContext(StaticContext);
  const tierNum = parseInt(tier, 10);

  return (
    <BasePage
      actionFlow="EnterpriseThankYou"
      pageId="EnterpriseThankYou"
      legacyWeb="respweb"
      includeHeader={false}
      includeFooter={false}
      theme={theme}
    >
      <Helmet>
        <base target="_top" href="/" />
        <title>{t(COPY.THANK_YOU)}</title>
        <meta name="description" content={t(COPY.THANK_YOU)} />
      </Helmet>
      <Container className="sm-enterprise-thank-you__outline" flexJustify="center" py={8}>
        {enterpriseThankYouHeader(logo)}
        {enterpriseThankYouBody(tierNum)}
        {enterpriseThankYouFooter()}
      </Container>
    </BasePage>
  );
};

export default EnterpriseThankYou;
